import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./FarmListPage.scss";
import BASE_URL from "../../../config/apiConfig";
import axios from "axios";
import { getUserEmail } from "../../../utils/auth";

const FarmListPage = () => {
  const [farms, setFarms] = useState([]);
  const [filteredFarms, setFilteredFarms] = useState([]);
  const [cropOptions, setCropOptions] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState("all");
  const [farmNameFilter, setFarmNameFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [farmToDelete, setFarmToDelete] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [isTrialUser, setIsTrialUser] = useState(false);

  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const fetchCrops = async () => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/crop-management/get-all-crops`,
      );
      setCropOptions(response.data || []);
    } catch (error) {
      console.error("Error fetching crops:", error);
    }
  };

  const fetchCustomerDetails = async () => {
    const userEmail = getUserEmail();

    if (!userEmail) return;

    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/get-customer-details`,
        { email: userEmail },
      );

      setIsTrialUser(response.data.is_trial);
    } catch (error) {
      console.error("Failed to fetch customer details:", error);
    }
  };

  const fetchFarms = async () => {
    const userEmail = getUserEmail();

    if (!userEmail) {
      console.error("User email is missing.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/farm-management/get-all-added-farms`,
        { email: userEmail },
      );

      const rawFarms = response.data;

      const farmsWithLocation = await Promise.all(
        rawFarms.map(async (farm) => {
          const locationDetails = await getLocationFromCoordinates(
            farm.coordinates.lat,
            farm.coordinates.lng,
          );
          return { ...farm, locationDetails };
        }),
      );

      const sortedFarms = farmsWithLocation.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );

      setFarms(sortedFarms);
      applyFilters(sortedFarms); // ✅ Filter right after setting farms
    } catch (error) {
      console.error("Failed to fetch farms:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCrops();
    fetchFarms();
    fetchCustomerDetails();
  }, []);

  const applyFilters = (farmList = farms) => {
    const filtered = farmList.filter((farm) => {
      const matchesCrop =
        selectedCrop === "all" || farm.crop_code === selectedCrop;

      return matchesCrop;
    });

    setFilteredFarms(filtered);
  };

  useEffect(() => {
    applyFilters(); // ✅ Runs whenever filters change
  }, [selectedCrop, farms]); // ✅ include farms for correct filtering

  const handleFarmClick = (farm_id, sowingDate, map_image_url) => {
    navigate(`/user/crop-health-management/farm/${farm_id}`, {
      state: { sowingDate, map_image_url },
    });
  };

  const handleRenewalPayment = (fieldId) => {
    navigate(`/user/renew-farm/farm/${fieldId}`, {
      state: { status: "renew" },
    });
  };

  const handleNewPayment = (farm) => {
    if (!farm) return;
    navigate("/payment", {
      state: {
        farm_id: farm.id,
        cropCode: farm.crop_code,
        cropName: farm.crop_name,
        farmName: farm.field_name,
        totalPrice: farm.total_price,
        cropType: farm.plan_name,
        sowingDate: farm.sowing_date,
        paymentFrequency: farm.payment_frequency,
        coordinates: farm.coordinates,
        fieldArea: farm.field_area,
      },
    });
  };

  const getLocationFromCoordinates = async (lat, lng) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`,
      );

      const result = response.data.results[0];
      const addressComponents = result.address_components;

      const state = addressComponents.find((comp) =>
        comp.types.includes("administrative_area_level_1"),
      )?.long_name;
      const town = addressComponents.find((comp) =>
        comp.types.includes("locality"),
      )?.long_name;

      return {
        display_name: result.formatted_address,
        state,
        town,
      };
    } catch (error) {
      console.error("Google reverse geocoding failed:", error);
      return null;
    }
  };

  return (
    <div className="farm-list-page">
      <div className="farm-list-header">
        <h1>My Farms</h1>

        <div className="farm-list-btn-group">
          {isLoading ? (
            <p></p>
          ) : (
            <>
              <button
                className="farm-list-add-btn"
                onClick={() => navigate("/user/add-farm-now")}
              >
                + Add Farm
              </button>

              {/* Conditionally render Add Trial Farm button when no farms are available */}
              {farms.length === 0 && isTrialUser && (
                <button
                  className="farm-list-trial-btn"
                  onClick={() => navigate("/user/trial-add-farm")}
                >
                  + Add Trial Farm
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* <div className="farm-list-filter">
        <label htmlFor="cropFilter">Filter by Crop: </label>
        <select
          id="cropFilter"
          value={selectedCrop}
          onChange={(e) => setSelectedCrop(e.target.value)}
        >
          <option value="all">All</option>
          {cropOptions.map((crop) => (
            <option key={crop.crop_code} value={crop.crop_code}>
              {crop.crop_name}
            </option>
          ))}
        </select>
      </div> */}

      {successMessage && (
        <div className="farm-list-success-message">{successMessage}</div>
      )}

      <div className="farm-list-container">
        {isLoading ? (
          <div className="farm-list-loading">Loading farms...</div>
        ) : filteredFarms.length > 0 ? (
          filteredFarms.map((farm) => (
            <div
              key={farm.field_id}
              className={`farm-list-item ${farm.plan_status !== "active" ? "farm-disabled" : ""}`}
              onClick={() => {
                if (farm.plan_status === "active") {
                  handleFarmClick(
                    farm.id,
                    farm.sowing_date,
                    farm.map_image_url,
                  );
                }
              }}
            >
              <div className="farm-list-content">
                <div className="farm-list-top">
                  <h2 className="farm-list-title">{farm.field_name}</h2>

                  {(farm.plan_status === "created" ||
                    farm.plan_status === "renew") && (
                    <button
                      className={`farm-list-pay-btn ${farm.plan_status}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        farm.plan_status === "created"
                          ? handleNewPayment(farm)
                          : handleRenewalPayment(farm.field_id);
                      }}
                    >
                      {farm.plan_status === "created" ? "Pay Now" : "Renew Now"}
                    </button>
                  )}
                  {farm.plan_status === "active" && (
                    <span className="farm-list-active-badge">Active</span>
                  )}
                </div>

                <div className="farm-list-map">
                  {farm.map_image_url && (
                    <a
                      href={`https://www.google.com/maps?q=${farm.coordinates.lat},${farm.coordinates.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="farm-map-link"
                    >
                      <img
                        src={farm.map_image_url}
                        alt="Farm Map"
                        className="farm-list-map-image"
                      />
                      <span className="map-overlay-btn">📍 View on Map</span>
                    </a>
                  )}
                </div>

                <div className="farm-list-details">
                  <p>
                    <strong>Crop:</strong> {farm.crop_name}
                  </p>
                  <p>
                    <strong>Area:</strong> {Number(farm.field_area).toFixed(2)}{" "}
                    ha
                  </p>
                  <p style={{ textTransform: "capitalize" }}>
                    <strong>Plan:</strong> {farm.plan_name}
                  </p>
                  <p>
                    <strong>
                      {farm.crop_name.toLowerCase() === "tea"
                        ? "Pruning Date:"
                        : "Sowing Date:"}
                    </strong>{" "}
                    {formatDate(farm.sowing_date)}
                  </p>
                  <p>
                    <strong>Purchased:</strong> {formatDate(farm.created_at)}
                  </p>
                  <p>
                    <strong>Frequency:</strong> {farm.payment_frequency}{" "}
                    Month(s)
                  </p>
                  <p>
                    <strong>Amount:</strong> ₹ {farm.total_price}
                  </p>
                  <p>
                    <strong>Coordinates:</strong> {farm.coordinates.lat},{" "}
                    {farm.coordinates.lng}
                  </p>
                  <p>
                    <strong>Location:</strong>{" "}
                    {farm.address ? farm.address : "N/A"}
                  </p>
                  {farm.plan_status !== "created" && (
                    <p>
                      <strong>Subscription Expiry:</strong>{" "}
                      {formatDate(farm.expires_at)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="farm-list-empty">
            No farms available right now. Please add your farm to get started.
          </p>
        )}
      </div>
    </div>
  );
};

export default FarmListPage;
